import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    IconButton,
    MenuItem,
    Button,
    Tooltip,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import RichEditorResultText from './Shared/RichEditorResultText';
import RichEditor from './Shared/RichEditor';
import {useGetIntakesQuery} from '../../../../../api/services/groupService';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import {useGetApplicationStatusesQuery} from '../../../../../api/services/schoolManagementService';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const IntakeChoice = ({
                          onChange,
                          index,
                          componentId,
                          fieldKey,
                          options,
                          value,
                          descriptionHtml,
                          onDescriptionHtmlChange,
                          commitDescriptionHtml,
                          enableValidation,
                          isEditMode,
                          isSubmitMode,
                          isFormPreviewMode,
                          isFormArchived,
                          relatedComponent,
                          accessLevel,
                          readOnly,
                          placeholderValues,
                          applicationStatusId,
                          error,
                          audience,
                          recipientType,
                          linkedToStudents,
                          submitMode
                      }) => {
    const {data: intakes = []} = useGetIntakesQuery();
    const {data: applicationStatuses = []} = useGetApplicationStatusesQuery({}, {skip: !isEditMode});

    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleOptionChange = (optionIndex, field, newValue) => {
        const updatedOptions = [...options];
        updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            [field]: newValue,
        };
        onChange({
            index,
            option: updatedOptions[optionIndex],
            optionId: updatedOptions[optionIndex].id,
        });
    };

    const addOption = () => {
        const newOption = {id: Date.now(), description: '', value: ''};
        onChange({
            index,
            isAdd: true,
            option: newOption,
        });
    };

    const removeOption = (optionIndex) => {
        const optionToRemove = options[optionIndex];
        onChange({
            index,
            isRemove: true,
            optionId: optionToRemove.id,
        });
    };

    const formatIntakeDisplay = (intake) => {
        return `${intake.admissionYear} - ${intake.season} - ${intake.yearGroup}`;
    };

    const renderEditMode = () => (
        <Box display="flex" flexDirection="column" sx={{mt: 2}}>
            <Box>
                <RichEditor
                    currentDescription={draftDescriptionHtml}
                    setCurrentDescription={handleDraftChange}
                    isFormLive={isFormArchived}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {options.length === 1 && (
                    <Alert severity="info" sx={{
                        mt: 2,
                        mb: 2
                    }}>
                        With only one option configured, this choice will not be visible in view mode and the option
                        will be automatically selected.
                    </Alert>
                )}
                <Box>
                    {options.map((option, optionIndex) => (
                        <Box
                            py={1}
                            display="flex"
                            justifyContent="space-between"
                            gap={1}
                            alignItems="center"
                            key={`intakeChoiceOption-${optionIndex}`}
                        >
                            <Typography>{optionIndex + 1}.</Typography>
                            <TextField
                                multiline
                                fullWidth
                                sx={{flex: 2}}
                                value={option.description}
                                disabled={isFormArchived}
                                label={!option.description ? 'Enter a choice for your respondents to select' : null}
                                InputLabelProps={{
                                    shrink: false,
                                }}
                                onChange={e => handleOptionChange(optionIndex, 'description', e.target.value)}
                            />
                            <TextField
                                select
                                required
                                sx={{flex: 1}}
                                error={!option?.value || (option?.value && !intakes.some(x => x.id === option.value))}
                                value={option.value || ''}
                                label="Intake"
                                onChange={e => handleOptionChange(optionIndex, 'value', Number(e.target.value))}
                            >
                                {intakes.map((intake) => (
                                    <MenuItem key={intake.id} value={`${intake.id}`}>
                                        {formatIntakeDisplay(intake)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip
                                title={relatedComponent?.targetState === option.id
                                    ? `This option cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                                    : null}
                                disabled={isFormArchived}
                            >
                                <IconButton>
                                    <DeleteIcon
                                        onClick={() =>
                                            relatedComponent?.targetState === option.id || isFormArchived
                                                ? {}
                                                : removeOption(optionIndex)
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
                <Button
                    onClick={addOption}
                    startIcon={<AddIcon/>}
                    cursor="pointer"
                    disabled={isFormArchived}
                >
                    Add option
                </Button>
            </Box>
            <RequiredCheckbox
                enableValidation={enableValidation}
                index={index}
                onChange={onChange}
                isFormArchived={isFormArchived || !!fieldKey}
            />
            <TextField
                select
                required
                fullWidth
                sx={{
                    marginTop: 2,
                    width: {
                        xs: '100%',
                        sm: '50%',
                    },
                }}
                error={applicationStatusId == null}
                value={applicationStatusId || ''}
                label="Application Status"
                onChange={e =>
                    onChange({
                        index,
                        applicationStatusId: Number(e.target.value),
                    })}
            >
                {applicationStatuses
                    .filter(s => s.category !== 'Admitted')
                    .map(({
                              id: statusId,
                              description: statusDescription
                          }) => (
                        <MenuItem
                            key={statusId}
                            value={`${statusId}`}
                        >
                            {statusDescription}
                        </MenuItem>
                    ))}
            </TextField>
        </Box>
    );

    const renderDisplayMode = () => {
        const displayOptions = readOnly
            ? options
            : options.filter(option => {
                const selectedIntake = intakes.find(i => i.id === option.value);
                return selectedIntake !== undefined && selectedIntake !== null;
            });

        if ((isFormPreviewMode || isSubmitMode) && !readOnly && (displayOptions.length === 1 || displayOptions.length === 0)) {
            return null;
        }

        return (
            <Box key={`intakeChoice-${index}`} sx={{mt: 2}}>
                <RichEditorResultText
                    id={componentId}
                    description={descriptionHtml}
                    placeholderValues={placeholderValues}
                    readOnly={readOnly}
                    enableValidation={enableValidation}
                    error={error}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    submitMode={submitMode}
                />
                <FormControl error={!!error} variant="standard">
                    <RadioGroup
                        value={value || ''}
                        onClick={e => onChange({
                            id: componentId,
                            radioValue: e.target.value === value ? null : e.target.value,
                        })}
                    >
                        {displayOptions.map((option, idx) => (
                            <FormControlLabel
                                key={`intakeChoiceOption-${idx}`}
                                sx={{
                                    '& .MuiTypography-root': {
                                        wordBreak: 'break-word',
                                        maxWidth: '800px',
                                    },
                                }}
                                value={option.id}
                                control={<Radio/>}
                                label={option.description}
                            />
                        ))}
                    </RadioGroup>
                    <FormHelperText>{error}</FormHelperText>
                </FormControl>
            </Box>
        );
    };

    useEffect(() => {
        if (!isEditMode && !readOnly) {
            const displayOptions = options.filter(option => {
                const selectedIntake = intakes.find(i => i.id === option.value);
                return selectedIntake !== undefined && selectedIntake !== null;
            });

            if (displayOptions.length === 1 && !value) {
                onChange({
                    id: componentId,
                    radioValue: displayOptions[0].id,
                });
            }
        }
    }, [isEditMode, options, value, intakes, readOnly, componentId, onChange]);

    return isEditMode ? renderEditMode() : renderDisplayMode();
};

IntakeChoice.propTypes = {
    onChange: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    componentId: PropTypes.string.isRequired,
    fieldKey: PropTypes.string,
    options: PropTypes.array.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isSubmitMode: PropTypes.bool,
    isFormPreviewMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    accessLevel: PropTypes.number,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    error: PropTypes.string,
    applicationStatusId: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default IntakeChoice;